export interface BidLot {
  name: string
  summary: string
  products: string[]
  useCases: string
}

export interface Bid {
  id: string
  reference: string
  title: string
  category: string
  publicationDate: string
  deadline: string
  status: 'open' | 'closing-soon' | 'closed'
  description: string
  estimatedValue?: string
  lots?: BidLot[]
}

// Derive the effective status from the deadline so bids auto-close once
// their deadline has passed — regardless of the authored status value.
export function getEffectiveStatus(
  bid: Bid,
): 'open' | 'closing-soon' | 'closed' {
  if (bid.status === 'closed') return 'closed'
  const now = new Date()
  const deadline = new Date(bid.deadline)
  // Normalise to end-of-day on the deadline date so the deadline day stays open
  deadline.setHours(23, 59, 59, 999)
  if (deadline.getTime() < now.getTime()) return 'closed'
  return bid.status
}

export const bids: Bid[] = [
  {
    id: '1',
    reference: 'AU-AFRI-2026-001',
    title:
      'Framework Agreement for Medical Equipment Supply to AU Health Partnership Programme (Lots 1–4)',
    category: 'Health & Medical',
    publicationDate: '2026-04-15',
    deadline: '2026-08-11',
    status: 'open',
    description:
      'Multi-year framework agreement for the supply of medical equipment in support of the AU Health Systems Strengthening Initiative across 15 member states. Awarded across four independent lots — suppliers may bid on one or more. Specialist single-lot bids from African manufacturers and regional health-sector SMEs are encouraged. All equipment must meet WHO and international medical-device safety standards.',
    estimatedValue: '£2,500,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — Diagnostic Equipment',
        summary: 'Imaging, point-of-care diagnostics, and laboratory analysers',
        products: [
          'Imaging systems (X-ray, ultrasound)',
          'ECG and ultrasound units',
          'Point-of-care diagnostic devices',
          'Laboratory analysers',
        ],
        useCases:
          'Primary care facilities and referral hospitals across AU member states.',
      },
      {
        name: 'Lot 2 — Surgical & Theatre Equipment',
        summary: 'Operating theatre, sterilisation, and anaesthesia equipment',
        products: [
          'Operating-theatre equipment',
          'Sterilisation systems',
          'Surgical instruments',
          'Anaesthesia machines',
        ],
        useCases: 'AU-supported hospitals and tertiary care centres.',
      },
      {
        name: 'Lot 3 — Laboratory & Pathology Equipment',
        summary: 'Laboratory instruments and reagent supply chains',
        products: [
          'Microscopes',
          'Centrifuges',
          'Sample-storage systems',
          'Reagent supply chains',
        ],
        useCases:
          'Diagnostic laboratories and disease-surveillance facilities.',
      },
      {
        name: 'Lot 4 — PPE & Medical Consumables',
        summary: 'Routine consumables and protective equipment',
        products: [
          'Gloves, masks, gowns',
          'Syringes and routine medical consumables',
          'Infection-control supplies',
        ],
        useCases:
          'Hospitals, field clinics, and emergency-response operations.',
      },
    ],
  },
  {
    id: '2',
    reference: 'AU-AFRI-2026-002',
    title: 'Construction of Trade Facilitation Centre in Accra, Ghana',
    category: 'Infrastructure',
    publicationDate: '2026-04-20',
    deadline: '2026-07-15',
    status: 'open',
    description:
      "Design-build single-contract tender for a modern Trade Facilitation Centre in Accra supporting AfCFTA implementation and Africa's global trade partnerships. Scope includes architectural design, civil and structural works, MEP, ICT infrastructure, fit-out, landscaping, and a 12-month defects-liability period. Bidders must demonstrate prior delivery of comparable institutional buildings, hold relevant ISO certifications, and submit a local-content plan prioritising Ghanaian and African subcontractors and labour.",
    estimatedValue: '£8,750,000',
  },
  {
    id: '22',
    reference: 'AU-AFRI-2026-020',
    title:
      'Framework Agreement for Door, Window & Building Access Systems (Lots 1–4)',
    category: 'Infrastructure',
    publicationDate: '2026-05-28',
    deadline: '2026-07-15',
    status: 'open',
    description:
      'Multi-year framework agreement for the supply of building access systems, safety doors, interior door systems, handrail systems, and aluminium window and door systems for the construction, fit-out, and operational support of the African Union Trade Facilitation Centre in Accra, Ghana. Awarded across four independent lots — suppliers may bid on one or more. African manufacturers, fabricators, and regional fit-out contractors are strongly encouraged. All products shall comply with applicable international safety, durability, fire-resistance, and building-performance standards.',
    estimatedValue: '£2,400,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — Safety Doors & Security Access Systems',
        summary:
          'Fire-rated, high-security, and heavy-duty institutional access doors',
        products: [
          'Steel safety doors',
          'Security entrance / access doors',
          'Fire-rated door systems',
          'Emergency exit doors',
          'Heavy-duty institutional entrance doors',
          'Multi-point locking door systems',
          'High-security building access systems',
        ],
        useCases:
          'Administrative offices, security-controlled areas, records rooms, technical facilities, service areas, emergency exits, and restricted-access sections of the Trade Facilitation Centre.',
      },
      {
        name: 'Lot 2 — Interior Door Systems',
        summary: 'Office, meeting-room, and acoustic interior doors',
        products: [
          'Interior swing doors',
          'Commercial office doors',
          'Meeting & conference room doors',
          'Staff office door systems',
          'Commercial interior partition doors',
          'Acoustic door systems',
          'Institutional room access doors',
        ],
        useCases:
          'Offices, conference rooms, meeting facilities, administrative departments, training rooms, and operational spaces within the Trade Facilitation Centre.',
      },
      {
        name: 'Lot 3 — Handrail & Building Safety Systems',
        summary:
          'Staircase, corridor, balcony, and accessibility railing systems',
        products: [
          'Aluminium handrail systems',
          'Staircase railing systems',
          'Corridor handrails',
          'Balcony guardrail systems',
          'Accessibility support rails',
          'Internal safety railings',
          'Architectural handrail solutions',
        ],
        useCases:
          'Staircases, walkways, corridors, balconies, public circulation areas, accessibility routes, and building safety infrastructure throughout the Trade Facilitation Centre.',
      },
      {
        name: 'Lot 4 — Aluminium Window & Door Systems',
        summary:
          'Aluminium windows, entrance doors, glazing, and façade openings',
        products: [
          'Aluminium window systems',
          'Aluminium entrance doors',
          'Sliding door systems',
          'Casement window systems',
          'Commercial glazing systems',
          'Energy-efficient window systems',
          'Institutional building façade openings',
        ],
        useCases:
          'Offices, conference facilities, reception areas, training centres, administrative buildings, public service areas, and structural façade components — chosen for durability, security, and long service life in institutional buildings.',
      },
    ],
  },
  {
    id: '23',
    reference: 'AU-AFRI-2026-021',
    title:
      'Framework Agreement for Security, Surveillance & Intelligent Building Systems (Lots 1–4)',
    category: 'Infrastructure',
    publicationDate: '2026-05-30',
    deadline: '2026-07-15',
    status: 'open',
    description:
      'Multi-year framework agreement for the supply, installation, and commissioning of security, surveillance, access-control, and intelligent building-security systems for the African Union Trade Facilitation Centre in Accra, Ghana. Awarded across four independent lots — suppliers may bid on one or more. African systems integrators, security-technology SMEs, and accredited regional installers are strongly encouraged. This is a brand-neutral tender: bids must specify products by technical performance and standards (no proprietary brand requirement). All systems must comply with applicable ONVIF, IEC, and international security and data-protection standards, and include integration, commissioning, and operator training.',
    estimatedValue: '£1,900,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — CCTV & Video Surveillance Systems',
        summary:
          'IP network cameras, intelligent cameras, and complete surveillance systems',
        products: [
          'IP network cameras (indoor)',
          'IP network cameras (outdoor)',
          'Intelligent / analytics-enabled cameras',
          'Complete CCTV surveillance systems',
          'PTZ and fixed-dome camera systems',
        ],
        useCases:
          'Building entrances and exits, reception and visitor areas, service counters, perimeters, parking facilities, and restricted zones of the Trade Facilitation Centre.',
      },
      {
        name: 'Lot 2 — Video Recording, Monitoring & Storage',
        summary:
          'NVR/DVR, monitoring consoles, and surveillance storage infrastructure',
        products: [
          'Network Video Recorders (NVR)',
          'Digital Video Recorders (DVR)',
          'Surveillance monitoring systems',
          'Surveillance storage infrastructure',
          'Security-grade hard drives and recording solutions',
        ],
        useCases:
          'Security control room and monitoring centre, ICT/data rooms, and centralised recording infrastructure.',
      },
      {
        name: 'Lot 3 — Access Control & Visitor Management',
        summary:
          'Access control, biometric entry, and building intercom systems',
        products: [
          'Access control systems',
          'Card access systems',
          'Biometric access control devices',
          'Electronic entry management systems',
          'IP video intercom and doorphone systems',
          'Visitor and building-entry communication systems',
        ],
        useCases:
          'Staff access and restricted areas, administrative offices, records storage, reception, and visitor processing.',
      },
      {
        name: 'Lot 4 — Security Alarm, Perimeter & Network Infrastructure',
        summary:
          'Alarms, intrusion and perimeter protection, and security network integration',
        products: [
          'Security alarm systems',
          'Intrusion detection systems',
          'Perimeter protection systems',
          'Motion detection systems',
          'Signal transmission and CCTV communication infrastructure',
          'Security network integration and connectivity equipment',
        ],
        useCases:
          'External building perimeter, loading and logistics zones, parking, and the security network backbone connecting all subsystems.',
      },
    ],
  },
  {
    id: '24',
    reference: 'AU-AFRI-2026-022',
    title:
      'Framework Agreement for ICT, Data Centre & Smart Building Technology Systems (Lots 1–4)',
    category: 'Technology',
    publicationDate: '2026-06-02',
    deadline: '2026-07-15',
    status: 'open',
    description:
      'Multi-year framework agreement for the supply, installation, integration, and commissioning of the ICT, data-centre, networking, communications, and digital-security technology backbone of the African Union Trade Facilitation Centre in Accra, Ghana. This framework covers the active electronic and IT systems of the Centre and complements the building construction (AU-AFRI-2026-002) and physical security & surveillance hardware (AU-AFRI-2026-021) frameworks. Awarded across four independent lots — suppliers may bid on one or more. African systems integrators, ICT SMEs, and accredited regional network and security specialists are strongly encouraged. This is a brand-neutral tender: bids must specify products by technical performance and standards (no proprietary brand requirement). All systems must comply with applicable ISO/IEC, TIA/EIA, ONVIF, and international data-protection and cybersecurity standards, and include design, integration, commissioning, documentation, and operator training.',
    estimatedValue: '£2,600,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — Data Centre & Network Infrastructure',
        summary:
          'Data-centre equipment, core networking, and structured cabling backbone',
        products: [
          'Data centre equipment (server racks, power, cooling, environmental monitoring)',
          'Network infrastructure (switches, routers, wireless access)',
          'Structured cabling systems (copper and fibre backbone)',
          'Network distribution and patching infrastructure',
        ],
        useCases:
          'Primary and secondary data/ICT rooms, communications risers, and the converged network backbone serving all building systems across the Trade Facilitation Centre.',
      },
      {
        name: 'Lot 2 — Unified Communications & Video Conferencing',
        summary:
          'Video conferencing, collaboration, and conference-room AV systems',
        products: [
          'Video conferencing infrastructure',
          'Conference and meeting-room collaboration systems',
          'Audio-visual presentation and display systems',
          'Unified communications and conferencing platforms',
        ],
        useCases:
          'Conference rooms, meeting facilities, training rooms, and executive briefing spaces supporting hybrid and multi-site collaboration.',
      },
      {
        name: 'Lot 3 — Cybersecurity & Network Protection Platforms',
        summary:
          'Network security, threat protection, and data-protection platforms',
        products: [
          'Cybersecurity platforms',
          'Network firewalls and intrusion-prevention systems',
          'Endpoint and email security solutions',
          'Security monitoring, logging, and threat-detection platforms',
        ],
        useCases:
          'Protection of the Centre’s ICT network, data centre, and digital services, including perimeter, endpoint, and centralised security operations.',
      },
      {
        name: 'Lot 4 — Integrated Electronic Security Systems (IP / Network Layer)',
        summary:
          'IP-networked CCTV, access control, biometrics, and security monitoring',
        products: [
          'CCTV systems (IP / network-integrated)',
          'Access control systems',
          'Biometric systems',
          'Security monitoring and management platforms',
        ],
        useCases:
          'The network-integrated digital layer of the Centre’s electronic security — connecting CCTV, access control, and biometrics into the converged ICT network and centralised security monitoring.',
      },
    ],
  },
  {
    id: '3',
    reference: 'AU-AFRI-2026-003',
    title:
      'Framework Agreement for Digital Skills Training Programme Delivery (Lots 1–3)',
    category: 'Education & Training',
    publicationDate: '2026-04-10',
    deadline: '2026-07-10',
    status: 'open',
    description:
      'Multi-year framework agreement to deliver digital skills training to 5,000 young professionals across AU member states. Awarded across three independent lots — providers may bid on one or more. African training institutions and SMEs are encouraged. All providers must demonstrate accredited curriculum and gender-inclusive delivery.',
    estimatedValue: '£1,200,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — AI, Data Science & Analytics',
        summary: 'Advanced AI, machine learning, and data analytics curriculum',
        products: [
          'AI & machine learning training tracks',
          'Data science and analytics curriculum',
          'Capstone projects with AU partner institutions',
        ],
        useCases:
          'Mid-career upskilling and emerging-talent acceleration programmes.',
      },
      {
        name: 'Lot 2 — Software Development & Cloud Engineering',
        summary: 'Full-stack and cloud engineering bootcamps with placements',
        products: [
          'Full-stack software development bootcamps',
          'DevOps and cloud engineering training',
          'Industry mentorship and employer placement pathways',
        ],
        useCases:
          'Graduate-level cohorts and career transitions into the tech sector.',
      },
      {
        name: 'Lot 3 — Digital Literacy & Foundational ICT',
        summary: 'Entry-level digital and productivity skills',
        products: [
          'Foundational digital literacy programmes',
          'Productivity tools training',
          'Entry-level ICT skills curriculum',
        ],
        useCases: 'Underserved youth in rural and peri-urban communities.',
      },
    ],
  },
  {
    id: '25',
    reference: 'AU-AFRI-2026-023',
    title:
      'Framework Agreement for Digital Transformation, E-Governance & Enterprise ICT Systems (Lots 1–6)',
    category: 'Technology',
    publicationDate: '2026-06-05',
    deadline: '2026-08-15',
    status: 'open',
    description:
      'Multi-year framework agreement for the design, development, supply, integration, and support of digital transformation, e-governance, enterprise management, information-security, data-analytics, and web/mobile application systems for the African Union Commission and supported member-state institutions. This framework covers digital platforms and software systems and is the systems-and-platforms companion to the Digital Skills Training Programme Delivery framework (AU-AFRI-2026-003), which covers human capacity building. Awarded across six independent lots — suppliers may bid on one or more. African software houses, ICT SMEs, systems integrators, and accredited regional development partners are strongly encouraged. This is a brand-neutral tender: bids must specify solutions by technical capability and standards (no proprietary brand requirement). All systems must comply with applicable ISO/IEC 27001 information-security, data-protection, interoperability, and accessibility standards, and include delivery, integration, documentation, training, and ongoing support.',
    estimatedValue: '£7,200,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — E-Governance & Digital Transformation Platforms',
        summary:
          'E-government, citizen services, and digital public-service platforms',
        products: [
          'E-government solutions',
          'Citizen service portals',
          'Digital public service platforms',
          'Electronic procedures (e-procedures) systems',
          'Government digital transformation platforms',
        ],
        useCases:
          'Modernising public administration and citizen-facing services for the AU Commission and member-state institutions.',
      },
      {
        name: 'Lot 2 — Digital Learning & Capacity-Building Platforms',
        summary:
          'Learning management, eLearning, and digital capacity-building systems',
        products: [
          'Learning Management Systems (LMS)',
          'eLearning platforms',
          'Online training systems',
          'Digital capacity-building platforms',
          'MK learning platform',
        ],
        useCases:
          'Hosting and delivering institutional training, knowledge management, and continuous professional development at scale.',
      },
      {
        name: 'Lot 3 — ERP & Enterprise Management Systems',
        summary:
          'ERP, budget, asset, inventory, and purchasing management systems',
        products: [
          'ERP solutions',
          'Budget management systems',
          'Asset management systems',
          'Inventory management systems',
          'Purchasing & procurement management systems',
        ],
        useCases:
          'Integrated management of institutional finance, assets, inventory, and procurement operations.',
      },
      {
        name: 'Lot 4 — Information Security & Business Continuity',
        summary:
          'Information security, data protection, monitoring, and continuity solutions',
        products: [
          'Information security systems',
          'Data protection solutions',
          'Security monitoring solutions',
          'Business continuity solutions',
        ],
        useCases:
          'Protecting institutional data and digital services and ensuring resilience and continuity of critical operations.',
      },
      {
        name: 'Lot 5 — Data Management, Analytics & M&E',
        summary:
          'Data management, statistical reporting, analytics, and performance dashboards',
        products: [
          'Data management platforms',
          'Statistical reporting systems',
          'Data analytics & visualisation',
          'Monitoring & evaluation (M&E) platforms',
          'Performance dashboards',
        ],
        useCases:
          'Evidence-based decision-making, programme monitoring, and transparent performance reporting across AU initiatives.',
      },
      {
        name: 'Lot 6 — Web & Mobile Application Development',
        summary: 'Web, mobile, and enterprise application development services',
        products: [
          'Web applications',
          'Mobile applications',
          'Government portals',
          'Business applications',
          'Enterprise digital platforms',
        ],
        useCases:
          'Custom application development for institutional, citizen-facing, and enterprise digital services.',
      },
    ],
  },
  {
    id: '4',
    reference: 'AU-AFRI-2026-004',
    title: 'Climate Finance Advisory Services',
    category: 'Consulting Services',
    publicationDate: '2026-03-28',
    deadline: '2026-06-05',
    status: 'closing-soon',
    description:
      'Single-contract advisory engagement for structuring climate finance instruments and green bonds for African infrastructure and renewable-energy projects. Scope includes market-sounding, instrument design, MRV frameworks, investor-engagement strategy, and capacity-building for AU member-state finance ministries. Bidders must demonstrate prior climate-finance transaction experience and field a multi-disciplinary team including African climate-finance specialists.',
    estimatedValue: '£650,000',
  },
  {
    id: '5',
    reference: 'AU-AFRI-2026-005',
    title:
      'Framework Agreement for Agricultural Technology Transfer Programme (Lots 1–3)',
    category: 'Agriculture',
    publicationDate: '2026-04-05',
    deadline: '2026-06-10',
    status: 'open',
    description:
      'Multi-year framework agreement for precision-agriculture technologies and training for smallholder farmers in East Africa. Awarded across three independent lots. African agri-tech SMEs and farmer-cooperative partnerships are strongly encouraged. Deliverables must align with climate-smart agriculture principles and the AU CAADP framework.',
    estimatedValue: '£3,200,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — Precision Agriculture Technology Supply',
        summary: 'Sensors, irrigation, satellite imagery, and farm software',
        products: [
          'Soil-sensor systems',
          'Irrigation-control equipment',
          'Satellite-imagery subscriptions',
          'Farm-management software platforms',
        ],
        useCases:
          'Equipping smallholder cooperatives with precision-ag capability.',
      },
      {
        name: 'Lot 2 — Farmer Training & Extension Services',
        summary: 'On-the-ground training and extension support',
        products: [
          'Local-language curriculum development',
          'Demonstration plot programmes',
          'Gender-inclusive participation models',
          'Extension officer training of trainers',
        ],
        useCases:
          'Field-level capacity building across East African districts.',
      },
      {
        name: 'Lot 3 — Mechanisation Equipment & Inputs',
        summary:
          'Small-scale mechanisation, seeds, and climate-resilient inputs',
        products: [
          'Small-scale mechanisation equipment',
          'Certified seeds',
          'Climate-resilient agricultural inputs',
        ],
        useCases:
          'Distributed through farmer-cooperative channels for equitable access.',
      },
    ],
  },
  {
    id: '6',
    reference: 'AU-AFRI-2025-089',
    title: 'Renewable Energy Infrastructure Assessment (10 Member States)',
    category: 'Energy',
    publicationDate: '2025-12-10',
    deadline: '2026-02-15',
    status: 'closed',
    description:
      'Single-contract feasibility and assessment engagement covering renewable-energy infrastructure needs across 10 AU member states. Scope included resource assessment (solar, wind, hydro, geothermal), grid-readiness analysis, regulatory benchmarking, investment-pipeline identification, and a consolidated continental opportunity report. Tender now closed; award outcome published on the Contract Awards page.',
    estimatedValue: '£450,000',
  },
  {
    id: '7',
    reference: 'AU-AFRI-2026-006',
    title: 'Diaspora Engagement Platform Development',
    category: 'Technology',
    publicationDate: '2026-04-18',
    deadline: '2026-06-05',
    status: 'open',
    description:
      'Single-contract software development engagement for the design, build, and deployment of a digital platform connecting the African diaspora worldwide with investment, partnership, and remittance-linked opportunities in Africa. Scope includes UX design, web and mobile applications, identity verification, secure payments integration, multi-language support (EN/FR/ES/AR/PT), WCAG 2.1 AA accessibility, a 24-month support and maintenance phase, and capacity transfer to an AU-designated operations team.',
    estimatedValue: '£850,000',
  },
  {
    id: '8',
    reference: 'AU-AFRI-2026-007',
    title:
      'Framework Agreement for Women Entrepreneurs Support Programme (Lots 1–3)',
    category: 'Economic Development',
    publicationDate: '2026-04-12',
    deadline: '2026-07-22',
    status: 'closing-soon',
    description:
      'Multi-year framework agreement to deliver a comprehensive support programme for 1,000 women entrepreneurs across AU member states. Awarded across three independent lots. African women-led organisations and SMEs are strongly encouraged. All providers must demonstrate measurable gender-impact outcomes and business-survival tracking.',
    estimatedValue: '£1,800,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — Business Training & Skills Development',
        summary:
          'Entrepreneurship, financial literacy, and sector-specific training',
        products: [
          'Tailored entrepreneurship training',
          'Financial-literacy programmes',
          'Sector-specific capacity-building',
          'In-person and online cohort delivery',
        ],
        useCases:
          'Foundational skills uplift for women-led ventures across all sectors.',
      },
      {
        name: 'Lot 2 — Mentorship & Networking',
        summary: 'Mentor pairings, peer networks, and regional summits',
        products: [
          'Structured mentorship pairings with senior African business leaders',
          'Peer-network programmes',
          'Regional networking summits',
        ],
        useCases:
          'Long-term relationship-building and ecosystem strengthening.',
      },
      {
        name: 'Lot 3 — Access to Finance Facilitation',
        summary: 'Investor matchmaking and finance facilitation',
        products: [
          'Investor matchmaking services',
          'Grant-application support',
          'Partnerships with African financial institutions',
          'Working capital and growth finance facilitation',
        ],
        useCases: 'Unlocking capital for scale-up of participating ventures.',
      },
    ],
  },
  {
    id: '9',
    reference: 'AU-AFRI-2025-092',
    title: 'Transport Corridor Feasibility Study (3 Member States)',
    category: 'Infrastructure',
    publicationDate: '2025-11-20',
    deadline: '2026-01-30',
    status: 'closed',
    description:
      'Single-contract feasibility engagement covering a multimodal transport corridor linking three AU member states. Scope included demand forecasting, route alternatives analysis, environmental and social impact screening, economic appraisal, financing-structure options, and a consolidated investment case. Tender now closed; award outcome published on the Contract Awards page.',
    estimatedValue: '£720,000',
  },
  {
    id: '10',
    reference: 'AU-AFRI-2026-008',
    title:
      'Framework Agreement for Youth Leadership Development Programme (Lots 1–3)',
    category: 'Education & Training',
    publicationDate: '2026-04-22',
    deadline: '2026-06-15',
    status: 'open',
    description:
      'Multi-year framework agreement to deliver leadership training and exchange opportunities for 200 emerging African leaders aged 25–35. Awarded across three independent lots. African training institutions, youth-led organisations, and pan-African networks are strongly encouraged. All providers must demonstrate measurable youth-empowerment outcomes and gender-balanced participation.',
    estimatedValue: '£950,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — Leadership Training & Curriculum Delivery',
        summary: 'Cohort-based leadership curriculum across regional hubs',
        products: [
          'Public-sector leadership curriculum',
          'Civic engagement and inclusive governance modules',
          'Regional hub delivery infrastructure',
        ],
        useCases:
          'Building a cohort of next-generation African public-sector leaders.',
      },
      {
        name: 'Lot 2 — International Exchange & Placements',
        summary: 'Exchange logistics and host-organisation matching',
        products: [
          'Host-organisation matching',
          'Cross-continental placement logistics',
          'Intra-African exchange programmes',
        ],
        useCases: 'Exposure to global and intra-African leadership ecosystems.',
      },
      {
        name: 'Lot 3 — Mentorship & Alumni Network',
        summary: 'Senior mentorship and alumni platform',
        products: [
          'Senior-leader mentorship pairings',
          'Alumni network platform',
          'Peer engagement and career-progression support',
        ],
        useCases: 'Long-term post-programme leadership ecosystem building.',
      },
    ],
  },
  {
    id: '17',
    reference: 'AU-AFRI-2026-015',
    title:
      'Framework Agreement for Healthcare Beds, Mattresses & Institutional Bedding (Lots 1–3)',
    category: 'Health & Medical',
    publicationDate: '2026-05-15',
    deadline: '2026-08-05',
    status: 'open',
    description:
      'Multi-year framework agreement for the supply of healthcare beds, mattresses, and institutional bedding to support AU healthcare, humanitarian, and staff-accommodation operations. Awarded across three independent lots. Specialist single-lot bids are encouraged to support African SME and regional manufacturer participation. All products must meet international safety, health, and fire-retardancy standards.',
    estimatedValue: '£3,750,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — Healthcare & Rehabilitation Beds',
        summary:
          'Adjustable hospital beds, orthopedic mattresses, healthcare pillows',
        products: [
          'Adjustable and lift-enabled hospital beds',
          'Motorised bed frames',
          'Orthopedic support mattresses',
          'Healthcare comfort pillows',
        ],
        useCases:
          'AU-supported hospitals, clinics, and rehabilitation centres.',
      },
      {
        name: 'Lot 2 — Humanitarian & Refugee Bedding',
        summary:
          'Vacuum-packed and compression mattresses for compact logistics',
        products: [
          'Vacuum-packed mattresses',
          'Compression foam mattresses',
          'Basic institutional bedding',
        ],
        useCases:
          'Refugee and IDP camp deployment, humanitarian field operations, emergency response.',
      },
      {
        name: 'Lot 3 — Staff Accommodation Bedding',
        summary: 'Dormitory-grade mattresses and pillows',
        products: [
          'Spring and foam mattresses',
          'Orthopedic and memory-foam pillows',
          'Dormitory-grade bedding',
        ],
        useCases:
          'AU regional office staff housing, training centres, peacekeeping accommodation, youth programme dormitories.',
      },
    ],
  },
  {
    id: '21',
    reference: 'AU-AFRI-2026-019',
    title:
      'Framework Agreement for Renewable, Hybrid & Backup Power Systems (Lots 1–5)',
    category: 'Energy',
    publicationDate: '2026-05-25',
    deadline: '2026-07-10',
    status: 'open',
    description:
      'Multi-year framework agreement for the supply of renewable energy generation, hybrid energy systems, resilient backup power, and institutional energy infrastructure to support AU healthcare, education, government, humanitarian, agricultural, and community development programmes across member states. The framework prioritises renewable and low-carbon generation, with backup generation included strictly to ensure operational resilience and a managed transition toward cleaner power, alongside an emerging hydrogen and zero-emission lot. Awarded across five independent lots — suppliers may bid on one or more. African renewable-energy manufacturers, EPC contractors, generator and power-systems specialists, and regional integrators are strongly encouraged. All systems must meet applicable IEC and international energy standards, carry a minimum 5-year performance warranty, and include local installation, commissioning, and maintenance training.',
    estimatedValue: '£9,800,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — Solar Power Generation Systems',
        summary:
          'Solar PV systems, power stations, and off-grid generation infrastructure',
        products: [
          'Solar photovoltaic (PV) power systems',
          'Solar power stations',
          'Solar energy generation equipment',
          'Off-grid solar generation solutions',
          'Institutional solar power infrastructure',
          'Renewable electricity generation systems',
        ],
        useCases:
          'Healthcare facilities, schools, government institutions, remote communities, humanitarian operations, and public infrastructure projects.',
      },
      {
        name: 'Lot 2 — Hybrid & Resilient Energy Systems',
        summary:
          'Solar/battery hybrids, microgrids, and battery-supported backup power',
        products: [
          'Generator + solar hybrid systems',
          'Generator + battery hybrid systems',
          'Microgrid power solutions',
          'Battery-supported backup power systems',
          'Distributed and resilient energy infrastructure',
          'Energy resilience solutions',
        ],
        useCases:
          'Institutions in areas with unstable or limited grid access requiring continuous, low-carbon, resilient power supply.',
      },
      {
        name: 'Lot 3 — Institutional & Community Energy Infrastructure',
        summary:
          'Decentralized, community-scale, and sustainable energy infrastructure',
        products: [
          'Renewable energy infrastructure systems',
          'Community power generation systems',
          'Institutional energy solutions',
          'Decentralized power systems',
          'Sustainable energy infrastructure',
          'Clean energy deployment projects',
        ],
        useCases:
          'Long-term institutional and community development programmes requiring decentralized clean-energy infrastructure.',
      },
      {
        name: 'Lot 4 — Backup Power Generation & Controls',
        summary:
          'Standby/prime generators (incl. HVO renewable fuel) with transfer, control, and monitoring systems',
        products: [
          'Industrial diesel generators',
          'Natural gas generators',
          'HVO renewable-fuel generators',
          'Prime power generators',
          'Standby power generators',
          'Mobile generator systems',
          'Automatic transfer switches (ATS)',
          'Generator controllers',
          'Power management systems',
          'Remote monitoring systems',
          'Switchgear systems',
        ],
        useCases:
          'Critical-load backup and operational resilience for healthcare, data, and administrative facilities — provided strictly as transitional/backup generation, prioritising HVO renewable fuel and hybridisation to reduce emissions.',
      },
      {
        name: 'Lot 5 — Hydrogen & Zero-Emission Backup Power',
        summary:
          'Hydrogen fuel cells, fuel-cell microgrids, and zero-emission backup power',
        products: [
          'Hydrogen fuel cell systems',
          'Zero-emission backup power solutions',
          'Fuel-cell microgrid systems',
          'Sustainable institutional power systems',
        ],
        useCases:
          'Forward-looking, zero-emission resilience for institutions pursuing clean backup power and long-term decarbonisation of critical-load supply.',
      },
    ],
  },
  {
    id: '20',
    reference: 'AU-AFRI-2026-018',
    title:
      'Framework Agreement for Mobility, Patient-Care & Emergency Health Equipment Supply (Lots 1–5)',
    category: 'Health & Medical',
    publicationDate: '2026-05-15',
    deadline: '2026-08-07',
    status: 'open',
    description:
      'Multi-year framework agreement for the supply of mobility, patient-care, rehabilitation, therapy, and emergency health equipment to AU-supported hospitals, rehabilitation centres, community health programmes, and humanitarian operations across member states. Awarded across five independent lots — suppliers may bid on one or more. African medical-device manufacturers, assistive-technology SMEs, and accredited regional distributors are strongly encouraged. All equipment must be CE/ISO 13485 certified, meet WHO assistive-product standards where applicable, and include user training and after-sales servicing arrangements.',
    estimatedValue: '£3,400,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — Mobility & Rehabilitation Support Equipment',
        summary:
          'Wheelchairs, walking aids, and patient mobility support devices',
        products: [
          'Manual wheelchairs',
          'Electric wheelchairs',
          'Pediatric wheelchairs',
          'Bariatric wheelchairs',
          'Mobility scooters',
          'Walking frames and rollators',
          'Crutches and walking sticks',
          'Patient transfer and mobility support equipment',
        ],
        useCases:
          'Hospitals, rehabilitation centres, disability support programmes, elderly care, humanitarian assistance, and public healthcare development projects.',
      },
      {
        name: 'Lot 2 — Hospital & Patient Care Support Equipment',
        summary:
          'Hospital beds, stretchers, commode and shower chairs, transfer equipment',
        products: [
          'Hospital beds',
          'Patient stretchers',
          'Commode chairs',
          'Shower chairs',
          'Lift and transfer chairs',
        ],
        useCases:
          'Hospitals, clinics, healthcare centres, rehabilitation facilities, emergency response programmes, and patient recovery facilities.',
      },
      {
        name: 'Lot 3 — Medical Devices & Emergency Health Products',
        summary:
          'Portable monitoring devices, oxygen support, and first-aid supplies',
        products: [
          'Hearing aids',
          'Blood pressure monitors',
          'Pulse oximeters',
          'Glucose monitoring devices',
          'Nebulizers',
          'First aid kits',
        ],
        useCases:
          'Community health projects, emergency medical support, public health campaigns, healthcare outreach, humanitarian operations, and institutional healthcare support systems.',
      },
      {
        name: 'Lot 4 — Rehabilitation, Physiotherapy & Electrotherapy Equipment',
        summary:
          'Circulation therapy, electrotherapy, and musculoskeletal rehabilitation devices',
        products: [
          'EMS foot care massagers',
          'Foot circulation therapy systems',
          'Lower-limb rehabilitation devices',
          'Blood circulation stimulation equipment',
          'TENS therapy devices (incl. wireless TENS units)',
          'EMS muscle stimulators',
          'Neuromuscular stimulation equipment',
          'Neck, lumbar, knee and hand therapy devices',
          'Therapeutic compression equipment',
          'Musculoskeletal support devices',
        ],
        useCases:
          'Rehabilitation centres, physiotherapy departments, pain-management clinics, post-injury and post-operative recovery programmes, and institutional rehabilitation services.',
      },
      {
        name: 'Lot 5 — Oxygen Therapy & Respiratory Support Equipment',
        summary:
          'Oxygen delivery and respiratory support for clinical and community settings',
        products: [
          'Oxygen concentrators',
          'Respiratory support equipment',
          'Community oxygen therapy systems',
          'Healthcare oxygen delivery devices',
          'Medical respiratory support solutions',
        ],
        useCases:
          'Hospitals, clinics, community health programmes, emergency respiratory support, and humanitarian oxygen-supply operations.',
      },
    ],
  },
  {
    id: '19',
    reference: 'AU-AFRI-2026-017',
    title:
      'Framework Agreement for Institutional Furniture & Fit-Out Supply (Lots 1–3)',
    category: 'Infrastructure',
    publicationDate: '2026-05-20',
    deadline: '2026-07-31',
    status: 'open',
    description:
      'Multi-year framework agreement for the supply, delivery, and installation of institutional furniture and fit-out across AU-supported buildings — including hospitals, schools, training centres, dormitories, staff accommodation, administrative offices, and rehabilitation facilities. Awarded across three independent lots; specialist single-lot bids from African manufacturers, joinery SMEs, and regional fit-out contractors are strongly encouraged. All products must meet international fire-safety, formaldehyde-emission, and durability standards, with a minimum 3-year manufacturer warranty.',
    estimatedValue: '£1,850,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — Institutional Kitchen Cabinet Systems',
        summary:
          'Modular kitchen cabinetry and storage for institutional facilities',
        products: [
          'Modular kitchen cabinet systems',
          'Institutional kitchen storage solutions',
          'Staff accommodation kitchen cabinets',
          'Educational facility kitchen cabinets',
          'Healthcare facility kitchen cabinetry',
          'Utility and storage cabinet systems',
        ],
        useCases:
          'Hospitals, schools, training centres, staff accommodation, dormitories, operational facilities, administrative buildings, and institutional infrastructure projects.',
      },
      {
        name: 'Lot 2 — Wardrobe & Storage Furniture Solutions',
        summary:
          'Built-in and modular wardrobes for accommodation and dormitories',
        products: [
          'Built-in wardrobe systems',
          'Modular wardrobe solutions',
          'Institutional storage cabinets',
          'Dormitory wardrobe units',
          'Accommodation storage furniture',
          'Staff housing wardrobe systems',
        ],
        useCases:
          'Accommodation facilities, dormitories, hospitals, staff residences, educational institutions, rehabilitation centres, and institutional housing programmes.',
      },
      {
        name: 'Lot 3 — Bathroom Vanity & Sanitary Furniture Systems',
        summary:
          'Vanity units and washroom cabinetry for institutional facilities',
        products: [
          'Bathroom vanity units',
          'Washroom cabinet systems',
          'Institutional bathroom furniture',
          'Healthcare facility vanity systems',
          'Modular bathroom storage units',
          'Accommodation bathroom furniture',
          'Public facility washroom cabinets',
        ],
        useCases:
          'Hospitals, accommodation facilities, schools, administrative buildings, staff housing projects, healthcare centres, and public infrastructure development programmes.',
      },
    ],
  },
  {
    id: '18',
    reference: 'AU-AFRI-2026-016',
    title:
      'Framework Agreement for Solar, LED & Smart Lighting Systems (Lots 1–8)',
    category: 'Energy',
    publicationDate: '2026-05-18',
    deadline: '2026-07-30',
    status: 'open',
    description:
      'Multi-year framework agreement for the supply, installation, and commissioning of solar, LED, industrial, architectural, and smart lighting systems — together with lighting controls and components — across AU member states. Covers both off-grid solar and grid-powered LED solutions for public infrastructure, institutional buildings, and community development programmes. Awarded across eight independent lots — suppliers may bid on one or more. African solar and LED manufacturers, integrators, and regional SMEs are strongly encouraged. All systems must meet IEC and applicable lighting standards, carry a minimum 5-year performance warranty, and include local installation and maintenance training.',
    estimatedValue: '£7,500,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — Solar Street Lighting Systems',
        summary:
          'Integrated solar street lights for roadways, highways, and public corridors',
        products: [
          'Integrated solar street lights',
          'High-power solar roadway lighting systems',
          'Smart solar street lighting solutions',
          'Motion-sensor solar street lights',
          'Energy-efficient roadway lighting systems',
          'Solar-powered highway and road lighting systems',
          'Institutional and municipal street lighting',
          'Public infrastructure solar lighting solutions',
        ],
        useCases:
          'Roadways, highways, public corridors, and municipal street networks in AU member states.',
      },
      {
        name: 'Lot 2 — Smart & Hybrid Solar Lighting Solutions',
        summary:
          'Smart, hybrid, and intelligently-controlled solar lighting infrastructure',
        products: [
          'Hybrid solar-grid lighting systems',
          'Smart remote-controlled solar lighting',
          'Auto-clean solar lighting systems',
          'Intelligent solar lighting infrastructure',
          'Bluetooth mesh lighting systems',
          'Smart energy-management lighting solutions',
          'Automated solar lighting control systems',
          'Advanced battery-managed solar lighting systems',
        ],
        useCases:
          'Smart-city pilot projects, hybrid grid environments, and intelligent infrastructure programmes.',
      },
      {
        name: 'Lot 3 — Solar Landscape & Public Area Lighting',
        summary:
          'Solar lighting for parks, gardens, walkways, and recreational areas',
        products: [
          'Solar landscape lighting systems',
          'Solar garden lighting',
          'Public park lighting systems',
          'Pedestrian walkway lighting',
          'Community and recreational area lighting',
          'Institutional outdoor lighting systems',
          'Hospitality and resort outdoor lighting solutions',
          'Decorative solar lighting systems',
        ],
        useCases:
          'Public parks, pedestrian zones, AU institutional grounds, and community recreational spaces.',
      },
      {
        name: 'Lot 4 — Solar Bollard & Commercial Lighting Systems',
        summary: 'Bollard, pathway, perimeter, and security solar lighting',
        products: [
          'Solar bollard lighting systems',
          'Commercial outdoor lighting',
          'Pathway and perimeter lighting systems',
          'Seaside and waterfront lighting solutions',
          'Smart tracking solar bollards',
          'Institutional commercial lighting systems',
          'Security and boundary lighting solutions',
        ],
        useCases:
          'Pathways, perimeters, security boundaries, and institutional commercial sites.',
      },
      {
        name: 'Lot 5 — Solar Infrastructure & Municipal Development Solutions',
        summary:
          'Large-scale municipal and infrastructure solar lighting projects',
        products: [
          'Municipal solar lighting infrastructure',
          'Smart-city lighting projects',
          'Public-sector lighting systems',
          'Affordable housing lighting infrastructure',
          'Community lighting development projects',
          'Road and transportation lighting systems',
          'Institutional infrastructure lighting projects',
          'Sustainable energy lighting solutions',
        ],
        useCases:
          'Municipal and city-scale lighting infrastructure programmes, affordable-housing developments, and integrated transport lighting projects.',
      },
      {
        name: 'Lot 6 — Indoor & Architectural LED Luminaires',
        summary:
          'LED panels, downlights, ceiling and linear luminaires for interiors',
        products: [
          'LED panels',
          'Downlights',
          'Ceiling luminaires',
          'Linear lighting systems',
          'Architectural interior luminaires',
        ],
        useCases:
          'Offices, conference and training rooms, healthcare and education facilities, and administrative interiors across AU institutional buildings.',
      },
      {
        name: 'Lot 7 — Industrial & High-Output Lighting',
        summary:
          'Floodlights, high-bay, and hermetic luminaires for demanding environments',
        products: [
          'Floodlights',
          'Solar floodlights',
          'High-bay industrial lighting',
          'Waterproof / hermetic luminaires',
          'Heavy-duty area and security floodlighting',
        ],
        useCases:
          'Warehouses, technical facilities, sports and event areas, perimeters, and outdoor/industrial environments requiring high-output, durable lighting.',
      },
      {
        name: 'Lot 8 — Smart Lighting Controls & LED Components',
        summary: 'DALI smart systems, drivers, controllers, and LED modules',
        products: [
          'DALI smart lighting systems',
          'LED drivers',
          'LED controllers',
          'LED modules',
          'Lighting management and control accessories',
        ],
        useCases:
          'Smart-building lighting management, energy-efficiency upgrades, and spare-parts/maintenance supply across AU facilities and infrastructure projects.',
      },
    ],
  },
  {
    id: '11',
    reference: 'AU-AFRI-2026-009',
    title: 'Framework Agreement for Food, Beverages & FMCG Supply (Lots 1–6)',
    category: 'Food, Beverages & FMCG',
    publicationDate: '2026-05-02',
    deadline: '2026-07-30',
    status: 'open',
    description:
      'Multi-year framework agreement for the supply of food, beverages, and FMCG products to the African Union Commission. Awarded across six broad, independent product lots designed to cover the full institutional FMCG portfolio — diplomatic gifting, summit hospitality, office catering, staff welfare, school feeding, refugee and IDP camp deployment, humanitarian field operations, and emergency feeding. Specialist single-lot bids from African SME and regional manufacturers are strongly encouraged. All suppliers must meet international food safety, labelling, and traceability standards.',
    estimatedValue: '£3,850,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — Hot Beverages',
        summary: 'Coffee, tea, and hot drink solutions',
        products: [
          'Instant coffee',
          'Ground coffee',
          'Ready coffee mixes',
          'Tea (black, green, herbal)',
          'Hot chocolate',
          'Institutional hot-beverage solutions',
        ],
        useCases:
          'AU headquarters, regional offices, summits, partner events, staff catering.',
      },
      {
        name: 'Lot 2 — Cold Beverages',
        summary:
          'Soft drinks, juices, energy & functional drinks, multipack supply',
        products: [
          'Carbonated soft drinks',
          'Flavoured beverages',
          'Zero-sugar drinks',
          'Juices',
          'Energy and functional drinks',
          'Performance / sports drinks',
          'Multipack beverage supply',
        ],
        useCases:
          'Summits, conferences, field operations, youth and sports programmes.',
      },
      {
        name: 'Lot 3 — Confectionery, Chocolate & Sweets',
        summary:
          'Diplomatic gifting confectionery + child welfare programme treats',
        products: [
          'Premium boxed chocolates (diplomatic gifting)',
          'Chocolate bars and tablets',
          'Chocolate snack bars',
          'Wafer chocolates',
          'Chocolate spreads',
          'Packaged confectionery',
          'Lollipops, fruit sweets, chewy confectionery',
          'Mint and breath-freshening products',
        ],
        useCases:
          'Diplomatic gifting, summit hospitality, school feeding, child welfare, humanitarian outreach (child-nutrition compliance where applicable).',
      },
      {
        name: 'Lot 4 — Biscuits, Cookies, Wafers & Snack Bars',
        summary: 'Sweet baked snacks and snack bars',
        products: [
          'Sandwich biscuits',
          'Cream-filled biscuits',
          'Wafer snacks',
          'Cookies',
          'Mini snack biscuits',
          'Granola bars',
          'Snack bars',
        ],
        useCases:
          'Office catering, meetings, conferences, youth programme refreshments.',
      },
      {
        name: 'Lot 5 — Savoury Snacks & Seeds',
        summary: 'Roasted seeds, nuts, savoury snacks and crackers',
        products: [
          'Roasted sunflower seeds',
          'Salted and unsalted nuts',
          'Packaged savoury snacks',
          'Savoury crackers',
          'Institutional refreshment products',
        ],
        useCases:
          'Staff welfare, hospitality, retail concessions within AU facilities, field-operation provisioning.',
      },
      {
        name: 'Lot 6 — Convenience Meals & Ready-to-Eat',
        summary:
          'Instant noodles, ready pasta, shelf-stable meals, emergency feeding',
        products: [
          'Instant noodles',
          'Cup and bowl noodles',
          'Ready-to-eat ramen meals',
          'Ready pasta meals',
          'Protein pasta products',
          'Convenience pasta bowls',
          'Shelf-stable meals',
          'Nutritionally balanced convenience meals',
          'Quick-service meal solutions',
        ],
        useCases:
          'Institutional catering, staff feeding programmes, training centres, emergency food support, humanitarian field deployments.',
      },
    ],
  },
]
